import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type TutorialId =
  | "device_onboarding"
  | "device_configuration"
  | "dashboard_panels"
  | "lichtblick_recordings";

export type TutorialStatus = "NONE" | "IN_PROGRESS" | "COMPLETED" | "SKIPPED";

export interface TutorialProgress {
  status: TutorialStatus;
  currentStep: number;
}

export interface TutorialState {
  /** Whether react-joyride should run */
  run: boolean;
  stepIndex: number;
  activeTutorial: TutorialId | null;
  /** Per-tutorial progress loaded from the API / localStorage */
  progress: Record<TutorialId, TutorialProgress>;
  /**
   * When the device_onboarding tutorial reaches the "registered-monitor"
   * step, this holds the device ID so MonitorButton can force itself visible.
   */
  monitorHighlightDeviceId: string | null;
}

const defaultProgress = (): Record<TutorialId, TutorialProgress> => ({
  device_onboarding: { status: "NONE", currentStep: 0 },
  device_configuration: { status: "NONE", currentStep: 0 },
  dashboard_panels: { status: "NONE", currentStep: 0 },
  lichtblick_recordings: { status: "NONE", currentStep: 0 },
});

const initialState: TutorialState = {
  run: false,
  stepIndex: 0,
  activeTutorial: null,
  progress: defaultProgress(),
  monitorHighlightDeviceId: null,
};

export const tutorialSlice = createSlice({
  name: "tutorial",
  initialState,
  reducers: {
    startTutorial: (
      state,
      action: PayloadAction<{ id: TutorialId; step?: number }>,
    ) => {
      const { id, step = 0 } = action.payload;
      state.activeTutorial = id;
      state.stepIndex = step;
      state.run = true;
      state.progress[id].status = "IN_PROGRESS";
      state.progress[id].currentStep = step;
    },
    setStep: (state, action: PayloadAction<number>) => {
      state.stepIndex = action.payload;
      if (state.activeTutorial) {
        state.progress[state.activeTutorial].currentStep = action.payload;
      }
    },
    advanceStep: (state) => {
      state.stepIndex += 1;
      if (state.activeTutorial) {
        state.progress[state.activeTutorial].currentStep = state.stepIndex;
      }
    },
    stopTutorial: (state) => {
      state.run = false;
      state.activeTutorial = null;
      state.stepIndex = 0;
    },
    completeTutorial: (state, action: PayloadAction<TutorialId>) => {
      state.progress[action.payload].status = "COMPLETED";
      state.monitorHighlightDeviceId = null;
      if (state.activeTutorial === action.payload) {
        state.run = false;
        state.activeTutorial = null;
        state.stepIndex = 0;
      }
    },
    skipTutorial: (state, action: PayloadAction<TutorialId>) => {
      state.progress[action.payload].status = "SKIPPED";
      state.monitorHighlightDeviceId = null;
      state.progress[action.payload].currentStep = 0;
      if (state.activeTutorial === action.payload) {
        state.run = false;
        state.activeTutorial = null;
        state.stepIndex = 0;
      }
    },
    setTutorialProgress: (
      state,
      action: PayloadAction<{ id: TutorialId; progress: TutorialProgress }>,
    ) => {
      state.progress[action.payload.id] = action.payload.progress;
    },
    setAllProgress: (
      state,
      action: PayloadAction<Partial<Record<TutorialId, TutorialProgress>>>,
    ) => {
      Object.assign(state.progress, action.payload);
    },
    setMonitorHighlightDevice: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.monitorHighlightDeviceId = action.payload;
    },
  },
});

export const {
  startTutorial,
  setStep,
  advanceStep,
  stopTutorial,
  completeTutorial,
  skipTutorial,
  setTutorialProgress,
  setAllProgress,
  setMonitorHighlightDevice,
} = tutorialSlice.actions;

export default tutorialSlice.reducer;
